import React, { useState, useEffect } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { Container, Box, CircularProgress, Typography, Paper } from '@mui/material';
import QuizTaker from '../components/quiz/QuizTaker';
import { getQuizByLinkToken, getQuizQuestions } from '../services/quizService';
import type { Quiz, QuizLink, QuizQuestion } from '../services/quizService';

const QuizPage: React.FC = () => {
  const { linkToken } = useParams<{ linkToken: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [quizLink, setQuizLink] = useState<QuizLink | null>(null);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);

  useEffect(() => {
    const loadQuiz = async () => {
      if (!linkToken) {
        setError('Invalid quiz link');
        setLoading(false);
        return;
      }

      try {
        // Fetch quiz and quiz link
        const data = await getQuizByLinkToken(linkToken);

        if (!data) {
          setError('Quiz not found');
          setLoading(false);
          return;
        }

        const { quiz: quizData, quizLink: linkData } = data;

        // Check if already completed
        if (linkData.completed_at) {
          navigate(`/quiz/${linkToken}/completed`, { replace: true });
          return;
        }

        // Check if expired
        const now = new Date();
        const expiresAt = new Date(linkData.expires_at);

        if (now > expiresAt) {
          navigate(`/quiz/${linkToken}/completed`, { replace: true });
          return;
        }

        // Load questions
        const questionsData = await getQuizQuestions(quizData.id);

        setQuiz(quizData);
        setQuizLink(linkData);
        setQuestions(questionsData);
        setLoading(false);
      } catch (err) {
        console.error('Error loading quiz:', err);
        setError('Failed to load quiz. Please check your connection and try again.');
        setLoading(false);
      }
    };

    loadQuiz();
  }, [linkToken, navigate]);

  const handleComplete = () => {
    navigate(`/quiz/${linkToken}/completed`, { replace: true });
  };

  if (!linkToken) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 8, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading quiz...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !quiz || !quizLink) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error || 'Quiz not available'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please contact the quiz administrator if you believe this is an error.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <QuizTaker
        quiz={quiz}
        quizLink={quizLink}
        questions={questions}
        onComplete={handleComplete}
      />
    </Container>
  );
};

export default QuizPage;
